'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
/**
 * Utilities for the storage and access of identifying information and authorization tokens
 * @namespace Storage
 * @example
 * import { Storage } from 'storis-jwt-auth';
 */

/**
 * Storage of email address
 * @namespace identity
 * @memberof Storage
 */

/**
 * Get the stored identity
 * @function
 * @name get
 * @return {object} Object containing email, name, and picture string attributes (null if not stored)
 * @memberof Storage.identity
 */
var getIdentity = function getIdentity() {
  return {
    email: localStorage.getItem('email'),
    name: localStorage.getItem('name'),
    picture: localStorage.getItem('picture')
  };
};

/**
 * Store identity
 * @function
 * @name set
 * @param {object} identity - Object containing identifying information from a user profile
 * @param {?string} identity.email - Email address
 * @param {?string} identity.name - Full name
 * @param {?string} identity.picture - Url of user avatar
 * @memberof Storage.identity
 */
var setIdentity = function setIdentity(_ref) {
  var _ref$email = _ref.email,
      email = _ref$email === undefined ? '' : _ref$email,
      _ref$name = _ref.name,
      name = _ref$name === undefined ? '' : _ref$name,
      _ref$picture = _ref.picture,
      picture = _ref$picture === undefined ? '' : _ref$picture;

  localStorage.setItem('email', email);
  localStorage.setItem('name', name);
  localStorage.setItem('picture', picture);
};

/**
 * Remove identity from storage
 * @function
 * @name remove
 * @memberof Storage.identity
 */
var removeIdentity = function removeIdentity() {
  localStorage.removeItem('email');
  localStorage.removeItem('name');
  localStorage.removeItem('picture');
};

/**
 * Storage of JSON Web Token (JWT) that is used for retrieving new access tokens
 * @namespace refreshToken
 * @memberof Storage
 */

/**
 * Get the stored refresh token
 * @function
 * @name get
 * @return {?string} Stored refresh token, null if not stored
 * @memberof Storage.refreshToken
 */
var getRefreshToken = function getRefreshToken() {
  return localStorage.getItem('refresh_token');
};

/**
 * Store a refresh token
 * @function
 * @name set
 * @param {string} refreshToken - Refresh token
 * @memberof Storage.refreshToken
 */
var setRefreshToken = function setRefreshToken(refreshToken) {
  localStorage.setItem('refresh_token', refreshToken);
};

/**
 * Remove refresh token from storage
 * @function
 * @name remove
 * @memberof Storage.refreshToken
 */
var removeRefreshToken = function removeRefreshToken() {
  localStorage.removeItem('refresh_token');
};

/**
 * Storage of JSON Web Token (JWT) that is used for authorization
 * @namespace accessToken
 * @memberof Storage
 */

/**
 * Get the stored access token
 * @function
 * @name get
 * @return {?string} Stored access token, null if not stored
 * @memberof Storage.accessToken
 */
var getAccessToken = function getAccessToken() {
  return localStorage.getItem('access_token');
};
/**
 * Store an access token
 * @function
 * @name set
 * @param {string} accessToken - Access token
 * @memberof Storage.accessToken
 */
var setAccessToken = function setAccessToken(accessToken) {
  localStorage.setItem('access_token', accessToken);
};

/**
 * Remove access token from storage
 * @function
 * @name remove
 * @memberof Storage.accessToken
 */
var removeAccessToken = function removeAccessToken() {
  localStorage.removeItem('access_token');
};

/**
 * Remove email address, access token, and refresh token from storage
 * @function
 * @memberof Storage
 */
var clear = function clear() {
  removeIdentity();
  removeRefreshToken();
  removeAccessToken();
};

exports.default = {
  clear: clear,
  identity: {
    get: getIdentity,
    set: setIdentity,
    remove: removeIdentity
  },
  accessToken: {
    get: getAccessToken,
    set: setAccessToken,
    remove: removeAccessToken
  },
  refreshToken: {
    get: getRefreshToken,
    set: setRefreshToken,
    remove: removeRefreshToken
  }
};